import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface HeaderSearchProps {
  className?: string;
}

export const HeaderSearch = ({ className }: HeaderSearchProps) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    navigate(`/topics?q=${encodeURIComponent(trimmed)}`);
    setQuery("");
    inputRef.current?.blur();
  };

  return (
    <form onSubmit={handleSubmit} className={cn("relative", className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <Input
        ref={inputRef}
        type="text"
        aria-label={t("nav.header.search", "Search topics")}
        placeholder={t("nav.header.searchPlaceholder", "Search topics...")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="h-8 w-full md:w-[140px] lg:w-[200px] pl-8 pr-8 text-sm bg-muted/50 border-border/50 focus-visible:ring-1 focus-visible:ring-primary/30"
      />
      <kbd className="hidden lg:inline-flex absolute right-2 top-1/2 -translate-y-1/2 h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[11px] font-medium text-muted-foreground pointer-events-none">
        <span className="text-xs">⌘</span>K
      </kbd>
    </form>
  );
};

export default HeaderSearch;
